import ActionTextIcon from "@/components/icons/ActionTextIcon";
import PopconfirmDeleteIcon from "@/components/icons/PopconfirmDeleteIcon";
import Ellipsis from "@/components/views/Ellipsis";
import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { formatLocaleDateTime } from "@/utils/formatUtils";
import { numberOrZero } from "@/utils/utils";
import { Dropdown, Popconfirm, Table } from "antd";
import { ItemType } from "antd/es/menu/interface";
import { ColumnsType } from "antd/es/table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath } from "react-router";
import { useDeleteDocumentSignature, useFilterDocumentsToSignForClient } from "../../../../common/api/queries";
import { PageSizes, TableSizes } from "../../../../common/constants";
import { UUID } from "../../../../typings/global";
import { DocSignatureStatusTag } from "../../../docsignature/components/DocSignatureStatusTag";
import { DocSignatureStatus, DocSignatureType, DocSignatureView } from "../../../docsignature/enums";
import { CLIENT_DOCUMENT_SIGN_PATHS } from "../../../docsignature/paths";
import { DocSignature } from "../../../docsignature/types";

enum DROPDOWN_ACTIONS {
  RESEND,
  ORIGINAL_FILE,
  SIGNED_FILE,
  DELETE,
}

type Props = {
  clientId: UUID;
  onResend: (document: DocSignature) => void;
  statusForFilter?: DocSignatureStatus[];
  typesForFilter?: DocSignatureType[];
};

export const ClientDocumentsToSignTable = ({ clientId, onResend, statusForFilter, typesForFilter }: Props) => {
  const { t } = useTranslation();

  const [tablePageIndex, setTablePageIndex] = useState(0);

  const { data, isFetching } = useFilterDocumentsToSignForClient({
    id: clientId,
    object: {
      view: DocSignatureView.AGENT,
      pageSize: PageSizes.SMALL,
      pageIndex: tablePageIndex,
      statuses: statusForFilter,
      types: typesForFilter,
    },
  });

  const deleteDocumentSignature = useDeleteDocumentSignature();

  const columns = useMemo((): ColumnsType<DocSignature> => {
    return [
      {
        key: "createdAt",
        title: t("documentSign.attrs.createdAt"),
        width: 160,
        render: (_, record) => formatLocaleDateTime(record.createdAt),
      },
      {
        key: "filename",
        title: t("documentSign.helpers.filename"),
        width: 250,
        ellipsis: { showTitle: false },
        render: (_, record) => <Ellipsis>{record.originalFile.filename}</Ellipsis>,
      },
      {
        key: "type",
        title: t("documentSign.enums.docSignatureType._label"),
        width: 130,
        render: (_, record) => t(`documentSign.enums.docSignatureType.${record.type}`),
      },
      {
        key: "status",
        title: t("documentSign.enums.docSignatureStatus._label"),
        width: 160,
        render: (_, record) => <DocSignatureStatusTag status={record.status} />,
      },
      {
        key: "oneTimeTokenValidUntil",
        title: t("documentSign.attrs.oneTimeTokenValidUntil"),
        width: 160,
        render: (_, record) =>
          record.oneTimeTokenValidUntil ? formatLocaleDateTime(record.oneTimeTokenValidUntil) : undefined,
      },
      {
        key: "signedAt",
        title: t("documentSign.attrs.signedAt"),
        width: 160,
        render: (_, record) => (record.signedAt ? formatLocaleDateTime(record.signedAt) : undefined),
      },
      {
        key: "actions",
        align: "right",
        fixed: "right",
        width: 130,
        render: (_, record) => {
          const items: ItemType[] = [];

          if (record.status === DocSignatureStatus.CREATED) {
            items.push(
              {
                key: DROPDOWN_ACTIONS.RESEND,
                label: <ActionTextIcon color="green" icon="reload" text={t("documentSign.actions.resend")} />,
                onClick: () => onResend(record),
              },
              {
                key: DROPDOWN_ACTIONS.ORIGINAL_FILE,
                label: (
                  <ActionTextIcon
                    path={generatePath(CLIENT_DOCUMENT_SIGN_PATHS.originalFile.to, { id1: clientId, id2: record.id })}
                    color="blue"
                    icon="file"
                    target="_blank"
                    text={t("documentSign.attrs.originalFile")}
                  />
                ),
              },
              {
                key: DROPDOWN_ACTIONS.DELETE,
                label: (
                  <Popconfirm
                    title={t("documentSign.helpers.deleteConfirm")}
                    icon={<PopconfirmDeleteIcon />}
                    cancelText={t("common.no")}
                    okText={t("common.yes")}
                    okType="danger"
                    onConfirm={() => deleteDocumentSignature.mutate({ id1: clientId, id2: record.id })}
                  >
                    <ActionTextIcon color="red" icon="delete" text={t("common.delete")} />
                  </Popconfirm>
                ),
              },
            );
          } else if (record.status === DocSignatureStatus.SIGNED) {
            items.push(
              {
                key: DROPDOWN_ACTIONS.ORIGINAL_FILE,
                label: (
                  <ActionTextIcon
                    path={generatePath(CLIENT_DOCUMENT_SIGN_PATHS.originalFile.to, { id1: clientId, id2: record.id })}
                    color="blue"
                    icon="file"
                    target="_blank"
                    text={t("documentSign.attrs.originalFile")}
                  />
                ),
              },
              {
                key: DROPDOWN_ACTIONS.SIGNED_FILE,
                label: (
                  <ActionTextIcon
                    path={generatePath(CLIENT_DOCUMENT_SIGN_PATHS.signedFile.to, { id1: clientId, id2: record.id })}
                    color="green"
                    icon="audit"
                    text={t("documentSign.attrs.signedFile")}
                    target="_blank"
                  />
                ),
              },
            );
          }

          return (
            <Dropdown menu={{ items }} trigger={["click"]}>
              <a onClick={(e) => e.preventDefault()}>
                <ActionTextIcon icon="menu-unfold" color="blue" text={t("common.options")} />
              </a>
            </Dropdown>
          );
        },
      },
    ];
  }, [t]);

  return (
    <Table<DocSignature>
      {...tableStandardProps()}
      scroll={{ x: TableSizes.LARGE }}
      loading={isFetching}
      columns={columns}
      dataSource={data?.pageData}
      pagination={{
        ...paginationTableProps,
        current: numberOrZero(data?.pageIndex) + 1,
        pageSize: data?.pageSize,
        total: data?.totalElementsCount,
        onChange: (pageIndex) => setTablePageIndex(pageIndex - 1),
      }}
    />
  );
};
